/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, PageGridContainer, Typography, Typography2, useTheme } from '@coursera/cds-core';

import { CML, CMLUtils } from 'bundles/cml';
import { PlaceholderShimmer } from 'bundles/skills-common';
import GoldStarSvg from 'bundles/skills-common/private/star-symbol/GoldStarSvg';

import _t from 'i18n!nls/skillset-learner-page';

const useStyles = () => {
  const { spacing, palette, breakpoints } = useTheme();
  return {
    wrapper: css`
      background-color: var(--cds-color-purple-975);
    `,
    topRowContent: css`
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: 0 !important; /* override CDS Grid item padding */

      > div {
        padding-left: 0 !important; /* override CDS padding for Breadcrumbs */
      }

      button {
        min-width: max-content;
      }
    `,
    header: css`
      display: flex;
      flex-direction: column-reverse;
    `,
    headerWrap: css`
      padding-top: 0 !important; /* override CDS Grid item padding */
    `,
    title: css`
      font-size: 48px;
      line-height: 60px;
      font-weight: 400;
    `,
    subtitle: css`
      display: flex;
      align-items: center;
      gap: ${spacing(12)};
      text-transform: uppercase;
    `,
    subtitleText: css`
      font-weight: 700;
    `,
    information: css`
      display: flex;
      padding-bottom: ${spacing(32)};
      align-items: end;
      flex-wrap: wrap;
    `,
    description: css`
      padding-right: ${spacing(64)};
      flex: 1 1 75%;

      ${breakpoints.down('sm')} {
        flex: 0 0 100%;
      }
    `,
    /* specifying the color to avoid a ssr issue with CML */
    descriptionText: css`
      font-size: 16px;
      color: ${palette.white};
    `,
    thirdPartyOrganization: css`
      flex: 1 0 25%;
      display: flex;
      flex-direction: column;
      align-items: end;
    `,
    thirdPartyOrganizationText: css`
      line-height: 20px;
      font-size: 12px;
      font-weight: 700;
    `,
  };
};

export type ThirdPartyOrganization = {
  logo?: string;
  name: string;
};
export type CMLDescription = {
  cml?: {
    dtdId: string;
    value: string;
  };
} | null;

type Props = {
  name: string;
  description: CMLDescription;
  thirdPartyOrganization: ThirdPartyOrganization;
  breadcrumb: JSX.Element;
  saveSkillsetButton?: JSX.Element;
};

const SkillSetTitleCard: React.FC<Props> = ({
  breadcrumb,
  saveSkillsetButton,
  name,
  description,
  thirdPartyOrganization,
}) => {
  const styles = useStyles();

  let providerElement = <PlaceholderShimmer width="150px" />;
  if (thirdPartyOrganization.logo) {
    providerElement = <img height={50} src={thirdPartyOrganization.logo} alt={thirdPartyOrganization.name} />;
  } else if (thirdPartyOrganization.name) {
    providerElement = (
      <Typography2 component="p" css={styles.thirdPartyOrganizationText} color="invertBody">
        {thirdPartyOrganization.name}
      </Typography2>
    );
  }

  return (
    <div css={styles.wrapper}>
      <PageGridContainer>
        <Grid item xs={12} css={styles.topRowContent}>
          {breadcrumb}
          {saveSkillsetButton}
        </Grid>
        <Grid item xs={12} css={styles.headerWrap}>
          <div css={styles.header}>
            <Typography
              css={styles.title}
              variant="h1"
              color="invertBody"
              aria-label={_t(`SkillSet: #{name}`, { name })}
            >
              {name || <PlaceholderShimmer width="300px" />}
            </Typography>
            <div css={styles.subtitle}>
              <GoldStarSvg height={26} width={22} />
              <Typography2 component="p" css={styles.subtitleText} variant="bodyPrimary" color="invertBody">
                {_t('SkillSet')}
              </Typography2>
            </div>
          </div>
          <div css={styles.information}>
            <div css={styles.description}>
              <Typography2 component="div" color="invertBody" css={styles.descriptionText}>
                {name ? (
                  <CML
                    cml={description?.cml ? CMLUtils.create(description.cml.value, description.cml.dtdId) : undefined}
                  />
                ) : (
                  <PlaceholderShimmer width="300px" height="100px" />
                )}
              </Typography2>
            </div>
            <div css={styles.thirdPartyOrganization}>
              <Typography2 component="p" css={styles.thirdPartyOrganizationText} color="invertBody">
                {_t('This Skill Objective was created by')}
              </Typography2>
              {providerElement}
            </div>
          </div>
        </Grid>
      </PageGridContainer>
    </div>
  );
};

export default SkillSetTitleCard;
